import { useEffect, useState } from 'react';
import { ApiError, type Api, type PlatformCallDetail, type PlatformCallRow, type VoiceHealth } from '../api';
import type { T } from '../i18n';
import { EvaluationCard, isEvaluation } from '../components/Evaluation';

const PAGE = 50;
const n = (v: number) => v.toLocaleString();
const usd = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `$${v.toFixed(v < 0.01 ? 4 : 3)}`);
const secs = (from: string | null, to: string | null) => (from && to ? Math.max(0, Math.round((Date.parse(to) - Date.parse(from)) / 1000)) : null);
const dur = (s: number | null) => (s === null ? '—' : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`);
const when = (iso: string) => new Date(iso).toLocaleString(undefined, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const errText = (t: T, code: string) => (t(`error.${code}`) === `error.${code}` ? t('error.generic') : t(`error.${code}`));

/**
 * ADR-046, platform operators only: every call of every business — which AI models it used, how many tokens,
 * the estimated model cost — and one call in detail (per model, per turn: heard, said, speed, tokens).
 */
export const PlatformCallsPage = ({ api, t, callId, onOpen, onBack }: { api: Api; t: T; callId?: string; onOpen: (id: string) => void; onBack: () => void }) =>
  callId ? <CallView api={api} t={t} id={callId} onBack={onBack} /> : <ListView api={api} t={t} onOpen={onOpen} />;

/** BRAIN/35: the Turkish voice server (EMA) — on/off/unreachable, load, and the last hour's switches to the backup voice. */
const VoiceHealthCard = ({ api, t }: { api: Api; t: T }) => {
  const [h, setH] = useState<VoiceHealth>();
  useEffect(() => {
    const load = () => void api.voiceHealth().then(setH).catch(() => undefined);
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [api]);
  if (!h) return null;
  const state = !h.ema.enabled ? 'off' : h.ema.reachable && h.ema.ok ? 'on' : 'unreachable';
  return (
    <div className="card section-gap">
      <div className="row-between">
        <h3>{t('platform.voice.title')}</h3>
        <span className={`tag ${state === 'on' ? 'ok' : state === 'off' ? '' : 'warn'}`}>{t(`platform.voice.${state}`)}</span>
      </div>
      {state === 'on' && <p className="hint">{t('platform.voice.load', { active: h.ema.active ?? 0, max: h.ema.maxActive ?? 0, served: h.ema.served ?? 0, busy: h.ema.busy ?? 0 })}</p>}
      <p className="hint">{t('platform.voice.lastHour', { emaCalls: h.lastHour.emaCalls, fellBack: h.lastHour.emaCallsFellBack, fallbacks: h.lastHour.fallbacks, failed: h.lastHour.failed })}</p>
    </div>
  );
};

const ListView = ({ api, t, onOpen }: { api: Api; t: T; onOpen: (id: string) => void }) => {
  const [kind, setKind] = useState<'' | 'real' | 'room'>('');
  const [tenant, setTenant] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<PlatformCallRow[]>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    setRows(undefined);
    setError(undefined);
    const q = { offset: page * PAGE, ...(kind ? { kind } : {}), ...(tenant.trim() ? { tenantId: tenant.trim() } : {}) };
    const h = setTimeout(() => api.platformCalls(q).then((r) => setRows(r.calls)).catch((e: unknown) => setError(e instanceof ApiError ? e.code : 'generic')), tenant ? 400 : 0);
    return () => clearTimeout(h);
  }, [api, kind, tenant, page]);
  const total = (rows ?? []).reduce((a, r) => ({ in: a.in + r.usage.inputTokens, cached: a.cached + r.usage.cachedInputTokens, out: a.out + r.usage.outputTokens, usd: a.usd + r.usage.usd }), { in: 0, cached: 0, out: 0, usd: 0 });
  return (
    <section className="stats">
      <header className="page-head">
        <div>
          <h1>{t('platform.calls.title')}</h1>
          <p className="muted">{t('platform.calls.subtitle')}</p>
        </div>
      </header>
      <div className="stats-filters">
        <div className="segmented" role="group">
          {(['', 'real', 'room'] as const).map((k) => (
            <button key={k || 'all'} type="button" className={kind === k ? 'on' : ''} onClick={() => (setKind(k), setPage(0))}>{t(`platform.calls.kind.${k || 'all'}`)}</button>
          ))}
        </div>
        <input className="search" value={tenant} onChange={(e) => (setTenant(e.target.value), setPage(0))} placeholder={t('platform.calls.tenantFilter')} aria-label={t('platform.calls.tenantFilter')} />
      </div>
      <VoiceHealthCard api={api} t={t} />
      {error && <div className="banner error">{errText(t, error)}</div>}
      {rows && (
        <div className="tiles">
          <div className="tile"><span>{t('platform.calls.onPage')}</span><strong>{rows.length}</strong></div>
          <div className="tile"><span>{t('platform.calls.inputTokens')}</span><strong>{n(total.in)}</strong></div>
          <div className="tile"><span>{t('platform.calls.outputTokens')}</span><strong>{n(total.out)}</strong></div>
          <div className="tile"><span>{t('platform.calls.cost')}</span><strong>{usd(total.usd)}</strong></div>
        </div>
      )}
      {rows && rows.length === 0 && <div className="empty"><p>{t('platform.calls.empty')}</p></div>}
      {rows && rows.length > 0 && (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead><tr>
                <th>{t('platform.calls.date')}</th><th>{t('platform.calls.business')}</th><th>{t('platform.calls.agent')}</th><th>{t('platform.calls.type')}</th>
                <th className="num">{t('platform.calls.duration')}</th><th className="num">{t('platform.calls.turns')}</th><th>{t('platform.calls.models')}</th>
                <th className="num">{t('platform.calls.tokens')}</th><th className="num">{t('platform.calls.cost')}</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="clickable" onClick={() => onOpen(r.id)}>
                    <td className="nowrap">{when(r.createdAt)}</td>
                    <td>{r.tenantName ?? r.tenantId}</td>
                    <td>
                      {r.agentName ?? r.agentId}
                      {r.voiceStats && r.voiceStats.fallback + r.voiceStats.failed > 0 && <span className="tag warn" title={t('platform.calls.warn.voiceHint')}> ⚠ {t('platform.calls.warn.voice', { n: r.voiceStats.fallback + r.voiceStats.failed })}</span>}
                      {(r.maxFirstSpeakMs ?? 0) > 8000 && <span className="tag warn" title={t('platform.calls.warn.silenceHint')}> ⚠ {t('platform.calls.warn.silence', { s: Math.round((r.maxFirstSpeakMs ?? 0) / 1000) })}</span>}
                    </td>
                    <td className="nowrap">{r.isRoom ? <span className="tag warn">{t('platform.calls.room')}</span> : t(`direction.${r.direction === 'inbound' ? 'inbound' : 'outbound'}`)}</td>
                    <td className="num">{dur(secs(r.startedAt, r.endedAt))}</td>
                    <td className="num">{r.turns}</td>
                    <td className="mono small">{r.usage.models.length ? r.usage.models.join(', ') : '—'}</td>
                    <td className="num nowrap">{r.usage.models.length ? `${n(r.usage.inputTokens)} / ${n(r.usage.outputTokens)}` : '—'}</td>
                    <td className="num">{r.usage.models.length ? usd(r.usage.usd) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="hint">{t('platform.calls.hint')}</p>
        </div>
      )}
      <div className="row-between">
        <button type="button" className="secondary" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('platform.calls.newer')}</button>
        <button type="button" className="secondary" disabled={!rows || rows.length < PAGE} onClick={() => setPage((p) => p + 1)}>{t('platform.calls.older')}</button>
      </div>
    </section>
  );
};

const CallView = ({ api, t, id, onBack }: { api: Api; t: T; id: string; onBack: () => void }) => {
  const [d, setD] = useState<PlatformCallDetail>();
  const [error, setError] = useState<string>();
  useEffect(() => void api.platformCall(id).then(setD).catch((e: unknown) => setError(e instanceof ApiError ? e.code : 'generic')), [api, id]);
  const role = (r: string) => (t(`platform.calls.role.${r}`) === `platform.calls.role.${r}` ? r : t(`platform.calls.role.${r}`));
  return (
    <section className="stats">
      <button type="button" className="link back" onClick={onBack}>← {t('platform.calls.back')}</button>
      {error && <div className="banner error">{errText(t, error)}</div>}
      {d && (
        <>
          <header className="page-head">
            <div>
              <h1>{d.call.agent_name ?? d.call.agent_id} · {d.call.tenant_name ?? d.call.tenant_id}</h1>
              <p className="muted mono small">{d.call.id}</p>
            </div>
          </header>
          <div className="tiles">
            <div className="tile"><span>{t('platform.calls.date')}</span><strong>{when(d.call.created_at)}</strong></div>
            <div className="tile"><span>{t('platform.calls.type')}</span><strong>{d.call.is_room ? t('platform.calls.room') : t('platform.calls.kind.real')} · {t(`direction.${d.call.direction === 'inbound' ? 'inbound' : 'outbound'}`)}</strong></div>
            <div className="tile"><span>{t('platform.calls.duration')}</span><strong>{dur(secs(d.call.started_at, d.call.ended_at))}</strong></div>
            <div className="tile"><span>{t('platform.calls.cost')}</span><strong>{usd(d.usage.usd)}</strong></div>
            <div className="tile"><span>{t('platform.calls.inputTokens')}</span><strong>{n(d.usage.inputTokens)}</strong></div>
            <div className="tile"><span>{t('platform.calls.cached')}</span><strong>{n(d.usage.cachedInputTokens)}</strong></div>
            <div className="tile"><span>{t('platform.calls.outputTokens')}</span><strong>{n(d.usage.outputTokens)}</strong></div>
            <div className="tile"><span>{t('platform.calls.modelCalls')}</span><strong>{n(d.usage.calls)}</strong></div>
          </div>
          {d.call.analysis?.summary && <p><span className="muted">{t('platform.calls.summary')}:</span> {d.call.analysis.summary} {d.call.analysis.score && <span className="tag ok">{d.call.analysis.score}</span>}</p>}
          {d.room && <p className="muted small">{t('platform.calls.scenario')} ({d.room.persona}): {d.room.scenario}</p>}
          {isEvaluation(d.call.evaluation) && <EvaluationCard t={t} e={d.call.evaluation} />}

          <h2>{t('platform.calls.modelsUsed')}</h2>
          {d.lines.length === 0 ? (
            <p className="muted">{t('platform.calls.noUsage')}</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr>
                  <th>{t('platform.calls.job')}</th><th>{t('platform.calls.model')}</th><th className="num">{t('platform.calls.modelCalls')}</th>
                  <th className="num">{t('platform.calls.inputTokens')}</th><th className="num">{t('platform.calls.cached')}</th><th className="num">{t('platform.calls.outputTokens')}</th>
                  <th className="num">{t('platform.calls.avgTime')}</th><th className="num">{t('platform.calls.cost')}</th>
                </tr></thead>
                <tbody>
                  {d.lines.map((l, i) => (
                    <tr key={i}>
                      <td>{role(l.role)}</td>
                      <td className="mono small">{l.provider}/{l.model}</td>
                      <td className="num">{l.calls}{l.estimatedCalls ? <span className="muted small"> ({t('platform.calls.estimated', { n: l.estimatedCalls })})</span> : null}</td>
                      <td className="num">{n(l.inputTokens)}</td>
                      <td className="num">{n(l.cachedInputTokens ?? 0)}</td>
                      <td className="num">{n(l.outputTokens)}</td>
                      <td className="num">{l.calls ? `${(l.ms / l.calls / 1000).toFixed(1)} s` : '—'}</td>
                      <td className="num">{usd(l.usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {d.lines.some((l) => l.estimatedCalls) && <p className="hint">{t('platform.calls.estimatedHint')}</p>}
          {d.usage.unpriced.length > 0 && <p className="hint">{t('platform.calls.unpriced', { models: d.usage.unpriced.join(', ') })}</p>}

          <h2>{t('platform.calls.conversation')}</h2>
          <p className="muted small">{t('platform.calls.conversationHint')}</p>
          <div className="turn-list">
            {d.turns.length === 0 && <p className="muted">{t('platform.calls.noTurns')}</p>}
            {d.turns.map((x, i) => (
              <div key={i} className="card turn-card">
                <div className="muted small turn-meta">
                  <span>#{x.turnIndex}</span>
                  {x.model && <span className="mono">{x.model}</span>}
                  {x.firstSpeakMs !== null && x.firstSpeakMs > 0 && <span>{t('platform.calls.firstWord', { s: (x.firstSpeakMs / 1000).toFixed(1) })}</span>}
                  {x.usage && <span>{t('platform.calls.turnTokens', { in: n(x.usage.inputTokens), out: n(x.usage.outputTokens), cached: n(x.usage.cachedInputTokens ?? 0) })}</span>}
                  {x.critics.length > 0 && <span>{t('platform.calls.critics', { list: x.critics.join(', ') })}</span>}
                </div>
                {(x.heard || x.event) && <p><b>{t('platform.calls.customer')}:</b> {x.heard || `(${x.event})`}</p>}
                <p><b>{t('platform.calls.agent')}:</b> {(x.said ?? []).join(' ')}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
};
